import React, { useEffect, useState, useContext, useRef } from "react";
import Header from "../../elements/Header";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  doc,
  getDoc,
  query,
  where,
  getDocs,
  collection,
  setDoc,
} from "firebase/firestore"; // Import Firestore functions
import { useFirestore } from "../../firebase/FirestoreContext"; // Import your Firestore context
import { collectionPath, EnvContext } from "../../context/EnvContext";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import { UserContext } from "../../context/UserContext"; // Import UserContext
import { getUserDocument } from "../../hooks/useDocument";
import { noop } from "motion";
import analyticsHelper from "../../utils/analyticsHelper";
import StickyFooter from "../../elements/StickyFooter";

const PaymentSuccess = () => {
  //
  const environment = useContext(EnvContext);
  const { user } = useContext(UserContext); // Get user from context
  //
  const location = useLocation();
  const navigate = useNavigate();
  const firestore = useFirestore(); // Get Firestore instance from context

  const { cart_sooperstock_ID } = location.state || {};
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCartID, setCurrentCartID] = useState(cart_sooperstock_ID); // Set initial cart_sooperstock_ID
  const [showStickyFooter, setShowStickyFooter] = useState(false); // State to control sticky footer visibility

  const [searchParams, setSearchParams] = useSearchParams();

  const continueShoppingRef = useRef(null);

  const sessionId = searchParams.get("session_id");
  const cartId = searchParams.get("cart_id");
  const isGoal = searchParams.get("goal") === "true";

  const transactionId = sessionId || cartId;

  const handleContinueShopping = () => {
    navigate("/"); // Adjust the path to your shop page
  };

  useEffect(() => {
    function checkContinueShoppingVisibility() {
      if (!continueShoppingRef.current) return;
      const rect = continueShoppingRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener("scroll", checkContinueShoppingVisibility, {
      passive: true,
    });
    window.addEventListener("resize", checkContinueShoppingVisibility);

    const timeoutId = setTimeout(checkContinueShoppingVisibility, 50);

    return () => {
      window.removeEventListener("scroll", checkContinueShoppingVisibility);
      window.removeEventListener("resize", checkContinueShoppingVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!continueShoppingRef.current) return;
      const rect = continueShoppingRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    if (!isGoal) return noop;
    if (!transactionId) return noop;
    if (!orderDetails) return noop;
    if (!orderDetails.items) return noop;

    try {
      const { shippingCost = 0, finalAmount, items, repeatOrderCheck } = orderDetails;

      analyticsHelper.purchase({
        transaction_id: transactionId,
        currency: "USD",
        value: finalAmount - shippingCost,
        shipping: shippingCost,
        coupon: orderDetails.applied_discount_name,
        repeat: repeatOrderCheck?.repeat || false,
        items: items.map((item) => ({
          item_id: item.seller_product_id,
          item_name: item.product_name,
          price: item.final_price,
          quantity: item.quantity,
        })),
      });
    } catch (error) {
      console.error("Failed to log purchase event");
      console.error(error);
    }

    setSearchParams((params) => {
      params.delete("goal");
      return params;
    });
  }, [orderDetails, transactionId]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!user) return;
      try {
        let orderData = null;

        // Handle sessionId-based fetching
        if (sessionId) {
          try {
            const cartDoc = await getUserDocument(
              {
                user,
                firestore,
                path: "Carts/CartsList/DataBase",
                userKey: "user_id",
              },
              where("stripeSessionId", "==", sessionId),
            );

            orderData = cartDoc.data();
            orderData.cartID = cartDoc.id; // Add the document ID to the data
          } catch (error) {
            if (error.name !== "DocumentNotFoundError") throw error;

            console.error("No cart found matching the provided sessionId.");
            setError("Order not found for the given session ID.");
            return;
          }
        }
        // Handle cartId-based fetching
        else if (cartId) {
          const cartDocRef = doc(
            firestore,
            `${collectionPath}Carts/CartsList/DataBase`,
            cartId,
          );
          const cartDocSnapshot = await getDoc(cartDocRef);

          if (cartDocSnapshot.exists()) {
            orderData = cartDocSnapshot.data();
            orderData.cartID = cartDocSnapshot.id; // Add the document ID to the data
          } else {
            console.error("No cart found matching the provided cartId.");
            setError("Order not found for the given cart ID.");
            return;
          }
        } else {
          throw new Error("Neither sessionId nor cartId provided.");
        }

        // Fetch items from the sub-collection
        if (orderData && orderData.cartID) {
          const itemsQuery = query(
            collection(
              firestore,
              `${collectionPath}Carts/CartsList/DataBase/${orderData.cartID}/Items`,
            ),
          );
          const itemsSnapshot = await getDocs(itemsQuery);

          // Map the items from the sub-collection
          const itemsArray = itemsSnapshot.docs.map((item) => ({
            id: item.id, // Document ID for the item
            ...item.data(),
          }));

          setCurrentCartID(orderData?.cart_sooperstock_ID); // Set the current cart ID

          orderData.items = itemsArray;
        } else {
          console.error("Order data is missing cartID.");
          setError("Invalid order data. Could not fetch items.");
          return;
        }

        const discountedItems = orderData.items.map((item) => ({
          ...item,
          discountedPrice: item.final_price * item.quantity,
        }));

        const totalPrice = (
          (orderData.subtotal || 0) -
          (orderData.applied_discount_amount || 0) +
          (orderData.shippingCost || 0)
        ).toFixed(2);

        // Update order details with discounted items and total price
        setOrderDetails({
          ...orderData,
          items: discountedItems,
          totalPrice,
        });
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error fetching order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [sessionId, cartId, firestore, collectionPath, user?.uid]);

  useEffect(() => {
    // Save purchased products to user's profile
    const savePurchasedProducts = async () => {
      if (!user || !orderDetails?.items) return;

      try {
        // Reference to the user's RecentlyPurchasedProducts collection
        const purchasedProductsRef = collection(
          firestore,
          `Users/UsersList/DataBase/${user.uid}/RecentlyPurchasedProducts`,
        );

        // Iterate through each purchased item and save it
        for (const item of orderDetails.items) {
          const productDocRef = doc(purchasedProductsRef, item.id); // Use item.id as document ID
          await setDoc(productDocRef, {
            ...item,
            date: new Date(),
            environment: environment,
          });
        }
        console.log("Purchased products saved to user profile.");
      } catch (error) {
        console.error("Error saving purchased products:", error);
      }
    };

    // Call the function to save purchased products
    if (!loading && !error && orderDetails) {
      savePurchasedProducts();
    }
  }, [loading, error, orderDetails, user, firestore, environment]);

  if (loading) {
    return <div>Loading...</div>; // Loading state
  }

  if (error) {
    return <div>{error}</div>; // Display error message
  }

  // Use orderDetails for rendering
  const { items: orderedItems, shippingCost = 0 } = orderDetails;

  // if shippingCost is not provided, set it to 0
  //shippingCost = shippingCost || 0;

  return (
    <div style={styles.container}>
      <Header search={false} />
      <div style={styles.content}>
        <div style={styles.animationContainer}>
          <dotlottie-player
            src="https://lottie.host/c86b1f88-9cf7-474c-8075-512aeecf1874/EQZ94rIPgj.json"
            background="transparent"
            speed="1"
            autoplay
            style={{ height: "220px", width: "220px" }}
          ></dotlottie-player>
        </div>
        <h2 style={{ ...styles.title, marginBottom: 50 }}>
          Thank you for your purchase!
        </h2>
        <div
          style={{
            background: "#f5f5f5",
            padding: "20px 20px",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <p style={styles.orderNumber}>Order ID: {currentCartID}</p>{" "}
          {/* Displaying the currentCartID */}
          <h3 style={styles.subtitle}>Items Ordered:</h3>
          <ul style={styles.itemsList}>
            {orderedItems &&
              orderedItems.map((item, index) => (
                <li
                  key={index}
                  style={{
                    ...styles.item,
                    display: "flex",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                  }}
                >
                  <div style={{ ...styles.itemDetails }}>
                    <img
                      src={
                        item?.images && item?.images?.length > 0
                          ? item?.images[0]
                          : "/images/no_product_image.png"
                      }
                      alt={item.product_name}
                      style={styles.itemImage}
                    />
                    <div>
                      <p style={styles.itemName}>{item?.brand_name}</p>
                      <p style={styles.itemName}>{item.product_name}</p>
                      <p style={{ ...styles.itemName, textAlign: "left" }}>
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p style={{ ...styles.itemPrice }}>
                    $
                    {(
                      item.discountedPrice ?? item.final_price * item.quantity
                    ).toFixed(2)}
                  </p>
                </li>
              ))}

            {shippingCost > 0 && (
              <li style={{ ...styles.item }}>
                <div style={styles.itemDetails}>
                  <div>
                    <p
                      style={{
                        ...styles.itemName,
                        padding: 0,
                        textAlign: "left",
                      }}
                    >
                      Shipping
                    </p>
                  </div>
                </div>
                <p style={styles.itemPrice}>
                  ${parseFloat(shippingCost).toFixed(2)}
                </p>
              </li>
            )}
            <li style={{ ...styles.item, borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
              <div style={styles.itemDetails}>
                <div>
                  <p
                    style={{
                      ...styles.itemName,
                      padding: 0,
                      textAlign: "left",
                    }}
                  >
                    Total
                  </p>
                </div>
              </div>
              <p style={styles.itemPrice}>${orderDetails.totalPrice}</p>
            </li>
          </ul>
        </div>
        <button
          ref={continueShoppingRef}
          style={styles.continueButton}
          onClick={handleContinueShopping}
        >
          Continue Shopping
        </button>
      </div>
      <FAQ />
      <Footer />
      {showStickyFooter && (
        <StickyFooter
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100vw",
            padding: "12px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 600,
              paddingLeft: 12,
              paddingRight: 12,
            }}
          >
            <button
              style={{ ...styles.continueButton, width: "100%" }}
              onClick={handleContinueShopping}
            >
              Continue Shopping
            </button>
          </div>
        </StickyFooter>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    minHeight: "100vh",
  },
  content: {
    padding: "20px",
    maxWidth: "600px",
    margin: "0 auto",
    textAlign: "center",
    fontFamily: "'Inter', sans-serif",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
  },
  animationContainer: {
    marginBottom: "20px",
    alignSelf: "center",
  },
  title: {
    fontSize: "24px",
    marginBottom: "10px",
  },
  orderNumber: {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: "30px",
    textAlign: "left",
  },
  subtitle: {
    fontSize: "1rem",
    marginBottom: "20px",
    textAlign: "left",
  },
  itemsList: {
    listStyleType: "none",
    padding: "0",
    marginBottom: "20px",
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    borderBottom: "1px solid #ddd",
    paddingBottom: "10px",
  },
  itemDetails: {
    display: "flex",
    alignItems: "center",
  },
  itemImage: {
    width: "50px",
    height: "50px",
    marginRight: "15px",
    objectFit: "cover",
    borderRadius: "5px",
  },
  itemName: {
    textAlign: "left",
    fontSize: "16px",
    marginBottom: "5px",
  },
  itemQuantity: {
    fontSize: "14px",
    color: "gray",
  },
  itemPrice: {
    fontSize: "16px",
    fontWeight: "bold",
  },
  continueButton: {
    width: "100%",
    padding: "12px ",
    backgroundColor: "black",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    borderRadius: "50px",
  },
};

export default PaymentSuccess;
